import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X, Loader2, Globe, Sparkles, BookOpen, GraduationCap, Heart, Brain, Lightbulb, Target } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'हिंदी (Hindi)' },
  { code: 'ta', name: 'தமிழ் (Tamil)' },
  { code: 'te', name: 'తెలుగు (Telugu)' },
  { code: 'bn', name: 'বাংলা (Bengali)' },
  { code: 'mr', name: 'मराठी (Marathi)' },
  { code: 'gu', name: 'ગુજરાતી (Gujarati)' },
  { code: 'kn', name: 'ಕನ್ನಡ (Kannada)' },
  { code: 'ml', name: 'മലയാളം (Malayalam)' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ (Punjabi)' },
  { code: 'or', name: 'ଓଡ଼ିଆ (Odia)' },
  { code: 'ur', name: 'اردو (Urdu)' },
  { code: 'ar', name: 'العربية (Arabic)' },
  { code: 'es', name: 'Español (Spanish)' },
  { code: 'fr', name: 'Français (French)' },
  { code: 'de', name: 'Deutsch (German)' },
];

const AryanMentorV2 = ({ isOpen, onClose, token, isProfessional = false }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [language, setLanguage] = useState('en');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);
  const [topicsDiscussed, setTopicsDiscussed] = useState([]);
  const [emotionalContext, setEmotionalContext] = useState(null);
  const messagesEndRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start chat session when opened
  useEffect(() => {
    if (isOpen && !sessionStarted && token) {
      startChatSession();
    }
  }, [isOpen, token, sessionStarted]);

  const startChatSession = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/aryan/v2/opening?language=${language}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages([{
          role: 'assistant',
          content: data.opening_message,
          timestamp: new Date().toISOString()
        }]);
        setSessionStarted(true);
      } else {
        // Fallback to V1 if V2 not available
        const fallbackResponse = await fetch(`${API_URL}/api/aryan/start?language=${language}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (fallbackResponse.ok) {
          const data = await fallbackResponse.json();
          setMessages([{
            role: 'assistant',
            content: data.opening_message,
            timestamp: new Date().toISOString()
          }]);
          setSessionStarted(true);
        }
      }
    } catch (error) {
      console.error('Error starting Aryan session:', error);
      setMessages([{
        role: 'assistant',
        content: "Hey there! 🙏✨ I'm Aryan, your personal mentor and career guide. I'm having a tiny technical moment - but I'm still here for you! Try sending a message and let's chat!",
        timestamp: new Date().toISOString()
      }]);
      setSessionStarted(true);
    }
    setIsLoading(false);
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      // Try V2 API first
      const response = await fetch(`${API_URL}/api/aryan/v2/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: messageToSend,
          language: language,
          emotional_context: emotionalContext
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          timestamp: data.timestamp
        }]);
        
        // Update context from response
        if (data.topics_discussed) {
          setTopicsDiscussed(data.topics_discussed);
        }
        if (data.emotional_context) {
          setEmotionalContext(data.emotional_context);
        }
      } else {
        // Fallback to V1 API
        const fallbackResponse = await fetch(`${API_URL}/api/aryan/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: messageToSend,
            language: language
          }),
        });
        
        if (fallbackResponse.ok) {
          const data = await fallbackResponse.json();
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: data.response,
            timestamp: data.timestamp
          }]);
        } else {
          throw new Error('Failed to get response');
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Oops! Something went a bit wonky on my end 😅 Could you try sending that again? I'm really interested in what you have to say!",
        timestamp: new Date().toISOString()
      }]);
    }
    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickActions = isProfessional ? [
    { icon: <Target size={16} />, text: "Leadership growth", prompt: "How can I enhance my leadership effectiveness based on my assessment?" },
    { icon: <Brain size={16} />, text: "Strategic thinking", prompt: "What does my report say about my strategic thinking capabilities?" },
    { icon: <Lightbulb size={16} />, text: "Career next steps", prompt: "Based on my profile, what should be my next career move?" },
  ] : [
    { icon: <BookOpen size={16} />, text: "Explore career paths", prompt: "What career paths would suit my personality and strengths?" },
    { icon: <GraduationCap size={16} />, text: "University guidance", prompt: "Tell me about the best universities for my interests" },
    { icon: <Sparkles size={16} />, text: "Understand my report", prompt: "Can you explain my assessment report in simple terms?" },
    { icon: <Heart size={16} />, text: "I'm feeling confused", prompt: "I'm feeling confused about my career - can we talk?" },
  ];

  const handleQuickAction = (prompt) => {
    setInputMessage(prompt);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 w-full max-w-2xl h-[85vh] rounded-2xl shadow-2xl border border-amber-500/30 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600/30 via-amber-500/20 to-amber-600/30 p-4 border-b border-amber-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                <span className="text-2xl">🧘</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-amber-300">Aryan</h2>
                <p className="text-sm text-amber-100/70">Your Mentor & Life Guide</p>
                {topicsDiscussed.length > 0 && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    Discussing: {topicsDiscussed.slice(0, 2).join(', ')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowLanguageSelect(!showLanguageSelect)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 transition-colors border border-slate-600/50"
                  data-testid="language-selector-v2"
                >
                  <Globe size={16} className="text-amber-400" />
                  <span className="text-sm">{LANGUAGES.find(l => l.code === language)?.name.split(' ')[0]}</span>
                </button>
                {showLanguageSelect && (
                  <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-10 py-1 min-w-[180px] max-h-60 overflow-y-auto">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setShowLanguageSelect(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-700 transition-colors ${
                          language === lang.code ? 'text-amber-400 bg-slate-700/50' : 'text-slate-300'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
                data-testid="close-aryan-v2"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && !isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-amber-500" />
                <p>Starting conversation...</p>
              </div>
            </div>
          )}
          
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
                  <span className="text-sm">🧘</span>
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-br-sm shadow-lg shadow-amber-500/20'
                    : 'bg-slate-700/60 text-slate-100 rounded-bl-sm border border-slate-600/50'
                }`}
              >
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mr-2 flex-shrink-0">
                <span className="text-sm">🧘</span>
              </div>
              <div className="bg-slate-700/60 rounded-2xl rounded-bl-sm px-4 py-3 border border-slate-600/50">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                  <span className="text-slate-400 text-sm">Aryan is thinking...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        {messages.length <= 2 && (
          <div className="px-4 pb-2">
            <p className="text-xs text-slate-400 mb-2">Quick topics to explore:</p>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickAction(action.prompt)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 text-sm transition-all hover:scale-105 border border-slate-600/50"
                  data-testid={`quick-action-${idx}`}
                >
                  {action.icon}
                  <span>{action.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 bg-slate-800/50 border-t border-slate-700/50">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Share what's on your mind..."
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 resize-none transition-all"
                rows={2}
                disabled={isLoading}
                data-testid="aryan-v2-input"
              />
            </div>
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg hover:shadow-amber-500/30 self-end"
              data-testid="aryan-v2-send"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            Aryan uses your assessment data to provide personalized guidance ✨
          </p>
        </div>
      </div>
    </div>
  );
};

export default AryanMentorV2;
